import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'

// Admin: user account create/edit — standalone page. With :id → edit,
// without → create. Creating an account also creates its self-participant
// (backend); the person then logs in via the normal email-code flow.

const blank = { name: '', email: '', phone: '', birthdate: '', language: 'ro', role: 'user', image_url: '' }

export function AdminUserFormPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { user: me, loading: authLoading } = useAuth()
  const isEdit = Boolean(id)
  const isSelf = isEdit && me && String(me.id) === String(id)

  const [v, setV] = useState(blank)
  const [loading, setLoading] = useState(isEdit)
  const [busy, setBusy] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  async function uploadAvatar(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true); setError(null)
    try {
      const d = await api.uploads.upload(file)
      setV((cur) => ({ ...cur, image_url: d.url }))
    } catch (err) {
      setError(err.code || 'server')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const set = (k) => (e) => setV((cur) => ({ ...cur, [k]: e.target.value }))

  useEffect(() => {
    if (authLoading || !me || me.role !== 'admin' || !isEdit) return
    api.admin.users.get(id)
      .then((d) => {
        const u = d.user
        setV({
          name: u.name || '', email: u.email || '', phone: u.phone || '',
          birthdate: u.birthdate || '',
          language: u.language || 'ro', role: u.role || 'user',
          image_url: u.image_url || '',
        })
      })
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }, [authLoading, me, id, isEdit])

  async function submit(e) {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      if (isEdit) {
        await api.admin.users.update(id, {
          name: v.name.trim(),
          email: v.email.trim(),
          phone: v.phone.trim() === '' ? null : v.phone.trim(),
          birthdate: v.birthdate || null,
          language: v.language,
          role: v.role,
          image_url: v.image_url.trim() === '' ? null : v.image_url.trim(),
        })
      } else {
        await api.admin.users.create({
          name: v.name.trim(),
          email: v.email.trim(),
          phone: v.phone.trim() === '' ? null : v.phone.trim(),
          birthdate: v.birthdate || null,
          language: v.language,
          role: v.role,
        })
      }
      navigate('/admin/users', { replace: true })
    } catch (err) {
      setError(err.code || 'server')
      setBusy(false)
    }
  }

  if (authLoading || loading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!me || me.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }

  return (
    <main className="container">
      <p><Link to="/admin/users">← {t('admin_users.back_to_list')}</Link></p>
      <h1>{isEdit ? t('admin_users.edit_title', { name: v.name }) : t('admin_users.add_title')}</h1>

      {error && (
        <p className="error">
          {t(`admin_users.errors.${error}`, { defaultValue: t(`auth.errors.${error}`, { defaultValue: error }) })}
        </p>
      )}

      <section className="card form-card">
        <form onSubmit={submit} noValidate>
          <label><span>{t('admin_users.form_name')}</span>
            <input value={v.name} onChange={set('name')} required minLength={2} /></label>
          <label><span>{t('admin_users.form_email')}</span>
            <input type="email" value={v.email} onChange={set('email')} required /></label>
          <label><span>{t('admin_users.form_phone')}</span>
            <input value={v.phone} onChange={set('phone')} /></label>
          <label><span>{t('admin_users.form_birthdate')}</span>
            <input type="date" value={v.birthdate} onChange={set('birthdate')} /></label>
          <label>
            <span>{t('admin_users.form_language')}</span>
            <select value={v.language} onChange={set('language')}>
              <option value="ro">{t('common.language_ro')}</option>
              <option value="en">{t('common.language_en')}</option>
            </select>
          </label>
          <label>
            <span>
              {t('admin_users.form_role')}
              {isSelf && <span className="muted small"> ({t('admin_users.cannot_change_own_role_hint')})</span>}
            </span>
            <select value={v.role} onChange={set('role')} disabled={isSelf}>
              <option value="user">{t('admin_users.role_user')}</option>
              <option value="teacher">{t('admin_users.role_teacher')}</option>
              <option value="admin">{t('admin_users.role_admin')}</option>
            </select>
          </label>
          {isEdit && (
            <label>
              <span>{t('admin_users.form_image')}</span>
              {v.image_url && (
                <img src={v.image_url} alt="" style={{ maxHeight: 100, marginBottom: '0.5rem', borderRadius: '50%', objectFit: 'cover', aspectRatio: '1 / 1', display: 'block' }} />
              )}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={uploadAvatar}
                disabled={uploading}
              />
              {uploading && <span className="muted small">{t('common.loading')}</span>}
            </label>
          )}
          <div className="form-actions">
            <button type="submit" disabled={busy || uploading}>{busy ? t('common.loading') : t('common.save')}</button>
            <Link to="/admin/users" className="link-button">{t('common.cancel')}</Link>
          </div>
        </form>
      </section>
    </main>
  )
}
